import { CustomCollapseCard, CustomPagination, CustomTable, Text } from '@src/components';
import { moneyFormat } from '@src/utils/utils';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLoadBalanceDetailsGetPagedList } from '@src/store/slice/balanceUploadDetailSlice';
import '@src/styles/balanceUpload/cardDetailInformation.scss';
import PropTypes from 'prop-types';
import { VouIdCode } from '@src/store/enums';

const CardDetailInformation = ({ isCompliments = false, isMarket = false }) => {
  const dispatch = useDispatch();
  const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);
  const { balanceUploadDetails, tableProperty } = useSelector(
    (state) => state?.balanceUploadDetail,
  );

  const vouId = useMemo(() => {
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [isMarket, isCompliments]);

  const loadBalanceDetailsGetPagedList = useCallback(
    async (pageNumber, pageSize) => {
      await dispatch(
        getLoadBalanceDetailsGetPagedList({
          body: {
            loadBalanceId: currentLoadBalance?.id,
            showOnlyPriced: true,
            vouId: vouId,
          },
          pageNumber,
          isFilter: false,
        }),
      );
    },
    [currentLoadBalance, isCompliments, isMarket, dispatch],
  );

  useEffect(() => {
    (async () => {
      await loadBalanceDetailsGetPagedList(1, 20);
    })();
  }, [loadBalanceDetailsGetPagedList]);

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await loadBalanceDetailsGetPagedList(pageNumber, pageSize);
    },
    [loadBalanceDetailsGetPagedList],
  );

  const nameColumn = {
    title: <Text t="nameUpperCase" />,
    dataIndex: 'name',
    key: 'name',
    width: 162.7,
    sorter: (a, b) => a.name.localeCompare(b.name),
  };

  const columnsCompliments = [
    nameColumn,
    {
      title: <Text t="surnameUpperCase" />,
      dataIndex: 'surname',
      key: 'surname',
      width: 162.7,
    },
    {
      title: <Text t="referenceCode3" />,
      dataIndex: 'refNo',
      key: 'refNo',
      width: 150,
      align: 'center',
    },
    {
      title: <Text t="cardNumber4" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 150,
      align: 'center',
    },
    {
      title: <Text t="amountVatIncludedUpperCase" />,
      dataIndex: 'price',
      key: 'price',
      width: 150,
      align: 'center',
      render: (text) => <span>{text ? `${moneyFormat(text, true)} ` : '0'}</span>,
    },
  ];
  const columnsNotCompliments = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 70,
      render: (_, __, index) =>
        (tableProperty?.currentPage - 1) * tableProperty?.pageSize + index + 1,
    },
    nameColumn,
    {
      title: <Text t="surnameUpperCase" />,
      dataIndex: 'surname',
      key: 'surname',
      width: 162.7,
    },
    {
      title: <Text t="referenceCode3" />,
      dataIndex: 'refNo',
      key: 'refNo',
      width: 150,
      align: 'center',
    },
    {
      title: <Text t="cardNumber4" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 150,
      align: 'center',
    },
    {
      title: <Text t="totalPrice" />,
      dataIndex: 'price',
      key: 'price',
      width: 150,
      align: 'center',
      render: (text) => <span>{text ? `${moneyFormat(text, true)} ` : '0'}</span>,
    },
  ];

  const columns = isCompliments || isMarket ? columnsCompliments : columnsNotCompliments;

  return (
    <CustomCollapseCard
      isExpand={!(isCompliments || isMarket)}
      cardTitle={<Text t="userListToLoadBalance" />}
      cardTitle2={isCompliments || isMarket ? <Text t="showUserListToLoadBalance" /> : null}
      className={
        isCompliments || isMarket
          ? 'comp-balance-upload-card-detail-information'
          : 'balance-upload-card-detail-information'
      }
    >
      {isCompliments || isMarket ? (
        <div
          style={{
            padding: 12,
            paddingTop: 16,
            paddingBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            flexDirection: 'row',
          }}
          className="comp-balance-users-head-text2"
        >
          <span className="comp-balance-users-head-text1">{currentLoadBalance?.totalCount}</span>{' '}
          <Text t="totalCreditAmountForUser2" />{' '}
          <span className="comp-balance-users-head-text1">
            {moneyFormat(currentLoadBalance?.totalPrice, true)}
          </span>
        </div>
      ) : (
        <div className="card-information">
          <span>{currentLoadBalance?.totalCount}</span> <Text t="totalCreditAmountForUser" />{' '}
          <span>{moneyFormat(currentLoadBalance?.totalPrice, true)}</span>
        </div>
      )}

      <CustomTable
        pagination={false}
        dataSource={balanceUploadDetails}
        columns={columns}
        rowKey={(record) => `balance-upload-summary-detail-list-${record?.id || record?.name}`}
      />

      {isCompliments || isMarket ? null : (
        <CustomPagination
          current={tableProperty?.currentPage}
          defaultCurrent={1}
          pageSize={tableProperty?.pageSize}
          total={tableProperty?.totalCount}
          onChange={async (pageNumber, pageSize) => {
            await handleTableChange(pageNumber, pageSize);
          }}
        />
      )}
    </CustomCollapseCard>
  );
};

CardDetailInformation.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
};

export default CardDetailInformation;
